import React from 'react';
import { Link } from 'react-router-dom';
import { FaUsers, FaImages, FaPalette, FaHeart, FaHistory, FaChartLine, FaHome } from 'react-icons/fa';
import { GatedLink, FEATURES } from '../FeatureGate';

/**
 * Reusable navigation link with tooltip title for mobile
 */
function NavLink({ to, icon, label, color, dataTestId }) {
  const colorClasses = {
    blue: 'text-blue-700 bg-blue-50 hover:bg-blue-100',
    amber: 'text-amber-700 bg-amber-50 hover:bg-amber-100',
    indigo: 'text-indigo-700 bg-indigo-50 hover:bg-indigo-100',
  };

  return (
    <Link
      to={to}
      className={`group relative flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 py-2 ${colorClasses[color]} rounded-lg transition text-xs sm:text-sm`}
      data-testid={dataTestId}
      title={label}
    >
      {icon}
      <span>{label}</span>
    </Link>
  );
}

/**
 * Family-specific navigation items
 */
export function FamilyNavItems({ family, familyId, userId }) {
  const isOwner = family?.created_by === userId;

  return (
    <>
      {/* Members Count */}
      <div className="flex items-center space-x-2 px-3 py-2 bg-blue-50 rounded-lg" data-testid="members-count">
        <FaUsers className="text-blue-600" />
        <span className="text-blue-700">{family?.members?.length || 0} {(family?.members?.length || 0) === 1 ? 'member' : 'members'}</span>
      </div>

      {/* Gallery Link */}
      <NavLink
        to={`/family/${familyId}/gallery`}
        icon={<FaImages />}
        label="Gallery"
        color="blue"
        dataTestId="gallery-link"
      />

      {/* Family Colors Link - Owner Only - Positioned prominently */}
      {isOwner && (
        <Link
          to={`/family/${familyId}?tab=settings`}
          className="group relative flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 py-2 text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-lg transition text-xs sm:text-sm"
          data-testid="family-colors-link"
          title="Family Colors"
        >
          <FaPalette />
          <span className="hidden lg:inline">Family Colors</span>
          <span className="lg:hidden">Colors</span>
        </Link>
      )}

      {/* Spiritual Link (Gated) */}
      <GatedLink
        feature={FEATURES.SPIRITUAL_FEATURES}
        to={`/family/${familyId}/spiritual`}
        className="group relative flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 py-2 text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-lg transition text-xs sm:text-sm"
        data-testid="spiritual-link"
        title="Spiritual"
      >
        <FaHeart />
        <span>Spiritual</span>
      </GatedLink>

      {/* Timeline Link */}
      <NavLink
        to={`/family/${familyId}/timeline`}
        icon={<FaHistory />}
        label="Timeline"
        color="amber"
        dataTestId="timeline-link"
      />

      {/* Analytics Link (Gated) */}
      <GatedLink
        feature={FEATURES.FAMILY_ANALYTICS}
        to={`/family/${familyId}/analytics`}
        className="group relative flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 py-2 text-green-700 bg-green-50 hover:bg-green-100 rounded-lg transition text-xs sm:text-sm"
        data-testid="analytics-link"
        title="Analytics"
      >
        <FaChartLine />
        <span>Analytics</span>
      </GatedLink>

      {/* Home Link */}
      <NavLink
        to="/"
        icon={<FaHome />}
        label="Home"
        color="indigo"
        dataTestId="home-link"
      />
    </>
  );
}
